// Manifest-specific helpers for the order-parent grid. Type-specific status semantics
// (inbound = received, outbound = staged) live in ./configs and are injected as a `statusModel`.

import { PALLET_STATUSES, PICKED_STATUSES, RECEIVED_STATUSES } from '@/constants/manifestStatuses';
import { isPlaceholderItem, isUnallocatedForwardStocked } from '@/utilities/forwardStocking';
import { canMarkReceived } from '@/components/InventoryUnit/utils';
import { formatQty } from '../utils';
import { applySearch } from '@/components/ExpandableGrid/gridOps';

export { formatQty };

export const hasOpenException = (item) =>
    !!item?.item_exception ||
    (item?.exceptions || []).some((mapping) => mapping?.exception?.status && mapping.exception.status !== 'RESOLVED');

export const getPalletDisplay = (pallet) =>
    pallet?.pallet_name || pallet?.pallet_number || (pallet?.pallet_id ? pallet.pallet_id.slice(0, 8) : null);

// Line ID prefers the shipper line id and otherwise a short internal id. Serial / barcode /
// location have no useful substitute, so they show an em dash when empty — never the UUID.
export const getItemIdDisplay = (item) => item?.line_id || (item?.item_id ? item.item_id.slice(0, 8) : '--');
export const getSerialDisplay = (item) => item?.serial_number || '—';
export const getBarcodeDisplay = (item) => item?.barcode || '—';
export const getLocationDisplay = (item) => item?.pallet?.warehouse_location || '—';

// Forward-stocked delivery orders hold placeholder item rows until allocation swaps them for the
// real warehouse items, at which point the order flips to ALLOCATED. Placeholders describe nothing
// physical, so they have no label to print.
export const PLACEHOLDER_PRINT_BLOCKED =
    'Item printing is disabled on this manifest because it has unallocated forward stocked inventory. Allocate inventory to print.';

export const placeholdersSkippedMessage = (count) =>
    `Skipped ${count} unallocated placeholder ${count === 1 ? 'item' : 'items'}`;

const unallocatedOrderIds = (manifest) =>
    new Map((manifest?.orders || []).filter(isUnallocatedForwardStocked).map((order) => [order.order_id, order]));

// An order part-way through allocation is a mixture: filled lines carry a real item with a pallet.
// Testing the order status alone would block printing for those real items.
const isPlaceholder = (item, unallocatedIds) => isPlaceholderItem(item, unallocatedIds.get(item?.order_id));

export const partitionPlaceholders = (manifest, items = []) => {
    const unallocated = unallocatedOrderIds(manifest);
    const printable = items.filter((item) => !isPlaceholder(item, unallocated));
    return { printable, skipped: items.length - printable.length };
};

export const allPlaceholdersSelected = ({ subject, selected }) =>
    (selected || []).every((item) => isPlaceholder(item, unallocatedOrderIds(subject)));

// Summarize an order's items (units) using the per-type status model: unit counts by category,
// qty-weighted segments for the progress bar, the summary chip, one-line status text, and the order's
// pallet + stored location (each single / Multiple / —). Shared by inbound (received) and outbound (staged).
export const summarizeItems = (items = [], statusModel) => {
    const { categories, classify, chip, statusText } = statusModel;
    const colorByKey = Object.fromEntries(categories.map((c) => [c.key, c.color]));

    const counts = Object.fromEntries(categories.map((c) => [c.key, 0]));
    const palletDisplays = new Set();
    const locations = new Set();
    let qty = 0;

    items.forEach((item) => {
        const units = item.quantity || 1;
        qty += units;
        counts[classify(item)] += units;
        const pallet = getPalletDisplay(item.pallet);
        if (pallet) palletDisplays.add(pallet);
        const location = item?.pallet?.warehouse_location;
        if (location) locations.add(location);
    });

    qty = formatQty(qty);
    categories.forEach((c) => {
        counts[c.key] = formatQty(counts[c.key]);
    });

    const segments = categories
        .filter((c) => counts[c.key] > 0)
        .map((c) => ({
            key: c.key,
            color: c.color,
            count: counts[c.key],
            percent: qty > 0 ? (counts[c.key] / qty) * 100 : 0,
        }));

    const chipResult = chip(counts, qty);
    const single = (values) => (values.length === 0 ? '—' : values.length === 1 ? values[0] : 'Multiple');

    return {
        qty,
        counts,
        segments,
        statusText: statusText(counts, qty),
        chip: { label: chipResult.label, color: colorByKey[chipResult.key], key: chipResult.key },
        pallet: single([...palletDisplays]),
        location: single([...locations]),
    };
};

// Total tracked units (of one category) across all orders — used for the header progress bar.
export const sumUnits = (orders = [], statusModel, categoryKey) =>
    formatQty(
        (orders || []).reduce(
            (sum, order) =>
                sum +
                (order.manifestItems || []).reduce(
                    (s, item) => s + (statusModel.classify(item) === categoryKey ? item.quantity || 1 : 0),
                    0
                ),
            0
        )
    );

// Total units across all orders whose pallet warehouse_status is in `statuses` — used for header
// progress bars that track a pallet-status milestone (e.g. inbound "stored") the statusModel's
// segmented categories don't distinguish.
export const sumUnitsByStatus = (orders = [], statuses = []) =>
    formatQty(
        (orders || []).reduce(
            (sum, order) =>
                sum +
                (order.manifestItems || []).reduce(
                    (s, item) => s + (statuses.includes(item?.pallet?.warehouse_status) ? item.quantity || 1 : 0),
                    0
                ),
            0
        )
    );

// Total units across all orders.
export const totalUnits = (orders = []) =>
    formatQty(
        (orders || []).reduce(
            (sum, order) => sum + (order.manifestItems || []).reduce((s, item) => s + (item.quantity || 1), 0),
            0
        )
    );

export const ORDER_ONLY_SEARCH_NOTE = 'No items match — showing all items';

// Order-level search text. Item fields live in itemSearchText so a term can match one line.
export const orderSearchText = (order) => {
    const source = order || {};
    return [
        source.order_number,
        source.po_number,
        source.dropoff_name,
        source.reference_id,
        source.order_shipper?.business_name,
    ]
        .filter(Boolean)
        .join(' ');
};

export const itemSearchText = (item) => {
    const source = item || {};
    return [
        source.sku,
        source.description,
        source.item_type_details,
        source.line_id,
        source.item_id,
        source.serial_number,
        source.barcode,
        getPalletDisplay(source.pallet),
    ]
        .filter(Boolean)
        .join(' ');
};

/**
 * True when the term would keep at least one order in the grid, using the grid's own search
 * (order text, then item text). A blank term matches.
 * @param {Array<object>} orders Orders as passed to the grid, each with `manifestItems`.
 * @param {string} term Search term.
 * @returns {boolean} Whether the grid would show at least one order for the term.
 */
export const termMatchesOrders = (orders, term) =>
    (orders || []).some(
        (order) =>
            applySearch({
                term,
                row: order,
                children: order.manifestItems || [],
                getOrderText: orderSearchText,
                getChildText: itemSearchText,
            }).include
    );

// Unique pallets across an order's items (by pallet_id). Used to decide when an order-level
// pallet action (e.g. pallet history) applies to exactly one pallet.
export const getOrderPallets = (items = []) => {
    const byId = new Map();
    (items || []).forEach((item) => {
        if (item?.pallet?.pallet_id && !byId.has(item.pallet.pallet_id)) {
            byId.set(item.pallet.pallet_id, item.pallet);
        }
    });
    return [...byId.values()];
};

export const isReceivable = (item) =>
    !RECEIVED_STATUSES.includes(item?.pallet?.warehouse_status) && !hasOpenException(item) && canMarkReceived(item);

// ---- Order-level unwind gates ----

export const orderHasReceived = (order) =>
    (order?.manifestItems || []).some((item) => RECEIVED_STATUSES.includes(item?.pallet?.warehouse_status));

// Events-only, so it also gates co-tenant orders fetched without manifestItems. Matches the
// transition, not the action: warehoused orders leave on RECEIVING:PICKING_UP, not START:PICKING_UP.
// (CD_PENDING_PO:PICKING_UP lands on CD_PENDING — an origin pickup, before the warehouse.)
export const orderStartedPickup = (order) =>
    (order?.wh_events || []).some((event) => event?.transition?.next === 'PICKED_UP');

// Blocks un-receive: a picked/staged unit has to be un-staged first.
export const orderPastReceiving = (order) =>
    (order?.manifestItems || []).some((item) => PICKED_STATUSES.includes(item?.pallet?.warehouse_status)) ||
    orderStartedPickup(order);

export const FORWARD_STOCKING_UNRECEIVE_BLOCKED = "Un-receive isn't supported for forward-stocked inventory yet";

// Also blocks un-receive, for a different reason: receive/store emit ITEM_* tracking_events and
// un-receive does not reverse them. forward_stocking_items_view derives an item's status from those
// events with `bool_or(event_type in (ITEM_RECEIVED_INTO_WAREHOUSE, ITEM_STORED_IN_WAREHOUSE,
// ITEM_RELOCATED_IN_WAREHOUSE))`, which is monotonic — once the event exists the flag can never go
// back. Un-receiving forward-stocked freight therefore leaves it readable as in-warehouse to the
// shipper's dashboard and to the mobile FIFO pick set, while the WMS says it never arrived.
//
// Reversing the events properly needs a compensating event type and a change to that view's status
// ladder (there is no delete_permissions block on tracking_events, and the insert fires a customer
// webhook a delete could not recall), so it is a separate ticket. Gated here until then.
//
// A line that carries an inventory_unit is locked too: its receive sets the unit's received_at, which
// un-receive does not clear, so the unit could not be received again while the register still counts it.
// forward_stocked_status as well as order_type: a delivery order's stock lines are grouped under it on
// the inbound manifest, where order_type is not 'wh_inventory'.
export const forwardStockingLocked = (order) =>
    order?.order_type === 'wh_inventory' ||
    !!order?.forward_stocked_status ||
    (order?.manifestItems || []).some((item) => item?.inventory_unit);

// Un-receive flips whole pallets, so the lock is checked on every order with an item on them. `rows` are
// the pallets' items, each with its order and inventory_unit.
export const lockedUnwindOrders = (orders, rows = []) =>
    orders.filter((order) =>
        forwardStockingLocked({ ...order, manifestItems: rows.filter((row) => row.order?.order_id === order.order_id) })
    );

// PICKED_UP excluded — Complete Pickup also completed the order.
export const orderHasStaged = (order) =>
    (order?.manifestItems || []).some((item) => item?.pallet?.warehouse_status === PALLET_STATUSES.STAGED);

export const orderReceivedPallets = (order) =>
    getOrderPallets(order?.manifestItems || []).filter((pallet) =>
        RECEIVED_STATUSES.includes(pallet?.warehouse_status)
    );

export const orderStagedPallets = (order) =>
    getOrderPallets(order?.manifestItems || []).filter((pallet) => pallet?.warehouse_status === PALLET_STATUSES.STAGED);

// Staging nulls the location, so the pallet log is the only record. Logs are newest-first.
export const lastStoredLocation = (pallet) =>
    (pallet?.logs || []).find((log) => log?.warehouse_status === PALLET_STATUSES.STORED && log?.warehouse_location)
        ?.warehouse_location || null;

// A consolidated pallet carries one warehouse_status, so every item must already agree — a mixed
// selection would silently promote or demote one group.
export const uniformReceivedStatus = (items = []) => {
    const received = items.filter((item) => RECEIVED_STATUSES.includes(item?.pallet?.warehouse_status)).length;
    return received === 0 || received === items.length;
};

// Units that can't be received stay off a shared pallet: any receive of that pallet would receive them too.
export const consolidatableItems = (items = []) =>
    items.filter((item) => RECEIVED_STATUSES.includes(item?.pallet?.warehouse_status) || canMarkReceived(item));

// SKU + description for the child "SKU / Description" column.
export const getSkuDescription = (item = {}) => {
    const desc = item.description || item.item_type_details || '';
    if (item.sku) return desc ? `${item.sku} · ${desc}` : item.sku;
    return desc || '--';
};
